import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StockService } from '../../services/stock.service';
import { StoreStockLot } from '../../interfaces/stock.interface';
import { Subject, takeUntil } from 'rxjs';
import { FormUtils } from 'src/app/core/utils/reactive-form';
import { StoreStockLotsService } from '../../services/store-stock-lots.service';

export interface StockLotDialogData {
  // stockLot?: StockLot;
  // productId: number;
  // storeId: number;
  // productName: string;
  // isEditMode: boolean;
  lotId: number;
}

interface StoreStockLotForm {
  lotId: FormControl<number>;
  productId: FormControl<number>;
  storeId: FormControl<number | null>;
  storeName: FormControl<string | null>;
  stockQuantity: FormControl<number>;
  dot: FormControl<string>;
  entryDate: FormControl<Date | null>;
  lotCode: FormControl<string | null>;
  notes: FormControl<string[]>;
  creationDate: FormControl<Date | null>;
  modificationDate: FormControl<Date | null>;
  price: FormControl<number>;
  locations: FormArray<FormControl<string>>;
  // reservedStock: FormControl<number>;
  // stockAvailable: FormControl<number>;
  reason: FormControl<number | null>;
  newNote: FormControl<string | null>;
}
export interface StoreStockLotFormValue {
  lotId: number;
  productId: number;
  storeId: number | null;
  storeName: string | null;
  stockQuantity: number;
  dot: string;
  entryDate: Date | null;
  lotCode: string | null;
  notes: string[];
  creationDate: Date | null;
  modificationDate: Date | null;
  price: number;
  locations: string[];
  reason: number | null;
  newNote: string | null;
}


export interface StoreStockLotUpdateDTO extends StoreStockLot {
  reason?: number;
  note?: string;
}

@Component({
  selector: 'app-stock-lot-modal',
  templateUrl: './stock-lot-modal.component.html',
  styleUrls: ['./stock-lot-modal.component.scss']
})
export class StockLotModalComponent implements OnInit {
  stockLotForm: FormGroup<StoreStockLotForm> | null = null
  isLoading = false;
  isEditMode = false;
  private readonly destroy$ = new Subject<void>();

  lot!: StoreStockLot;

  reasonOptions = [
    { value: 3, label: 'pages.stock.edit.reasons.loss_damage' },
    { value: 2, label: 'pages.stock.edit.reasons.inventory_adjustment' },
    { value: 1, label: 'pages.stock.edit.reasons.direct_sale' }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly storeStockLotsService: StoreStockLotsService,
    public dialogRef: MatDialogRef<StockLotModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StockLotDialogData
  ) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.storeStockLotsService.getStockLotById(this.data.lotId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: StoreStockLot) => {
        data.locations = this.cleanLocations(data.locations)
        this.stockLotForm = this.createForm(data)

      },
      error: () => {
        console.error("Error getting Lot info")
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  cleanLocations<T>(array: T[]): T[] {
    return array.filter(item => item !== '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get locations(): FormArray<FormControl<string>> {
    return this.stockLotForm?.controls?.locations as FormArray<FormControl<string>>;
  }

  private createForm(data: StoreStockLot): FormGroup<StoreStockLotForm> {
    return this.fb.group({
      lotId: this.fb.nonNullable.control<number>(data.lotId, Validators.required),
      productId: this.fb.nonNullable.control<number>(data.productId, Validators.required),
      storeId: this.fb.control<number | null>(data.storeId ?? null),
      storeName: this.fb.control<string | null>(data.storeName ?? null),
      stockQuantity: this.fb.nonNullable.control<number>(data.stockQuantity, [Validators.required, Validators.min(data.reservedStock ?? 0)]),
      price: this.fb.nonNullable.control<number>(data.price, [Validators.required, Validators.min(0)]),
      dot: this.fb.nonNullable.control<string>(data.dot, Validators.required),
      entryDate: this.fb.nonNullable.control<Date | null>(data.EntryDate ? new Date(data.EntryDate) : new Date(), Validators.required),
      lotCode: this.fb.nonNullable.control<string | null>(data.lotCode ?? null),
      notes: this.fb.nonNullable.control(data.notes ?? []),
      creationDate: this.fb.control<Date | null>(data.creationDate ? new Date(data.creationDate) : null),
      modificationDate: this.fb.control<Date | null>(data.modificationDate ? new Date(data.modificationDate) : null),

      locations: this.fb.array(
        (data.locations ?? []).map(loc => this.fb.nonNullable.control(loc)),
      ),
      reason: this.fb.control<number | null>(null, [Validators.required]),
      newNote: this.fb.control<string | null>('')
    });
  }


  onSubmit(): void {
    if (!this.stockLotForm) { return }
    if (this.stockLotForm.invalid || this.isLoading) {
      FormUtils.scrollToFirstError(this.stockLotForm)
      return;
    }
    if (this.stockLotForm.valid) {
      const formValue = this.buildUpdateRequest(this.stockLotForm);
      this.updateStockLot(formValue);
    }
  }

  buildUpdateRequest(formToUpdate: FormGroup<StoreStockLotForm>): StoreStockLotUpdateDTO {
    let form: StoreStockLotFormValue = formToUpdate.getRawValue();

    return {
      lotId: form.lotId,
      productId: form.productId,
      stockQuantity: form.stockQuantity,
      dot: form.dot,
      price: form.price,
      locations: form.locations,
      // reservedStock: form.reservedStock,
      // stockAvailable: form.stockAvailable,
      note: form.newNote ?? undefined
    }
  }

  private updateStockLot(lot: StoreStockLotUpdateDTO): void {
    this.isLoading = true;
    this.storeStockLotsService.updateStockLot(lot)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stockLot) => {
          this.isLoading = false;
          this.dialogRef.close(stockLot);
        },
        error: (error) => {
          console.error('Error updating stock lot:', error);
          this.isLoading = false;
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private dateToString(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}